import turfCircle from "@turf/circle";
import distance from "@turf/distance";
import GeoJSON from "geojson";
import { FixedShapeSupportedGeometry } from "../types/internal";
import { getCoordinateList, numbersEqualWithin, updateCoordinates } from "./geojson-helpers";

const CIRCLE_STEPS = 36;

/** Creates circle coordinates from a center and a point on the circumference, always producing 37 coordinates (36 + closing). */
export function createCircleFromCenterAndRadius(center: GeoJSON.Position, radius: number): GeoJSON.Position[] {
    const circleFeature = turfCircle(center, radius, { steps: CIRCLE_STEPS, units: "meters" });
    return circleFeature.geometry.coordinates[0];
}

export function getShapeCenter(geometry: FixedShapeSupportedGeometry): GeoJSON.Position {
    const coords = getCoordinateList(geometry);
    const uniqueCoords = coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords.slice(0, -1) : coords;

    if (uniqueCoords.length <= 1) {
        return uniqueCoords[0] || [0, 0];
    }

    const sumLng = uniqueCoords.reduce((sum, c) => sum + c[0], 0);
    const sumLat = uniqueCoords.reduce((sum, c) => sum + c[1], 0);
    return [sumLng / uniqueCoords.length, sumLat / uniqueCoords.length];
}

/** Gets the center of a circle geometry by averaging all coordinates */
export function getCircleCenter(geometry: FixedShapeSupportedGeometry): GeoJSON.Position {
    return getShapeCenter(geometry);
}

/** Gets the radius of a circle geometry in meters */
export function getCircleRadius(geometry: FixedShapeSupportedGeometry): number {
    const center = getCircleCenter(geometry);
    const coords = getCoordinateList(geometry);
    if (coords.length < 2) return 0;
    return distance(center, coords[0], { units: "meters" });
}

/** Resizes a circle during drawing - determines the radius from center to target coordinate */
export function resizeCircle(currentGeom: FixedShapeSupportedGeometry, targetCoord: GeoJSON.Position): FixedShapeSupportedGeometry {
    const listOfCoordinates = getCoordinateList(currentGeom);
    if (currentGeom.coordinates.length === 0 || listOfCoordinates.every((coord) => numbersEqualWithin(targetCoord, coord, 0))) {
        return {
            ...currentGeom,
            coordinates: [currentGeom.type === "Polygon" ? [targetCoord, targetCoord] : targetCoord],
        } as FixedShapeSupportedGeometry;
    }

    // The center is the first coordinate placed
    const center = getCircleCenter(currentGeom);
    const radius = distance(center, targetCoord, { units: "meters" });

    const coords = createCircleFromCenterAndRadius(center, radius);
    return updateCoordinates(currentGeom, coords) as FixedShapeSupportedGeometry;
}

/** Resizes a circle by setting a new radius value (used in object details) */
export function resizeCircleByRadius(geometry: FixedShapeSupportedGeometry, newRadius: number): FixedShapeSupportedGeometry {
    const center = getCircleCenter(geometry);
    const coords = createCircleFromCenterAndRadius(center, newRadius);
    return updateCoordinates(geometry, coords);
}

/** Resizes a circle when a node is dragged - adjusts radius based on new node position */
export function resizeCircleFromNodeDrag(geometry: FixedShapeSupportedGeometry, newCoordinate: GeoJSON.Position): FixedShapeSupportedGeometry {
    const center = getCircleCenter(geometry);
    const newRadius = distance(center, newCoordinate, { units: "meters" });
    const coords = createCircleFromCenterAndRadius(center, newRadius);
    return updateCoordinates(geometry, coords);
}
